/**
 * @module index
 * @package @geenius/adapters/react-css
 * @description Implements the React CSS adapter provider and context access
 * helpers. This module owns adapter status initialization for the React CSS
 * runtime package while keeping the shared package framework-agnostic.
 */

"use client";

import {
  ADAPTER_DOMAINS,
  AdapterContextError,
  type AdapterDomain,
  AdapterError,
  type AdapterStatusInfo,
  type AdminAdapter,
  type AiAdapter,
  type AuthAdapter,
  createConsoleLoggerAdapter,
  createLocalStorageAdminAdapter,
  createLocalStorageAiAdapter,
  createLocalStorageAuthAdapter,
  createLocalStorageDbAdapter,
  createLocalStorageFileAdapter,
  createLocalStoragePaymentsAdapter,
  type DbAdapter,
  DOMAIN_LABELS,
  type FileStorageAdapter,
  getDomainConfig,
  isAdaptersConfigured,
  type LoggerAdapter,
  type PaymentsAdapter,
} from "@geenius/adapters";
import {
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type AnyAdapter =
  | DbAdapter
  | AuthAdapter
  | PaymentsAdapter
  | AiAdapter
  | FileStorageAdapter
  | AdminAdapter
  | LoggerAdapter;

type AdapterForDomain<D extends AdapterDomain> = D extends "db"
  ? DbAdapter
  : D extends "auth"
    ? AuthAdapter
    : D extends "payments"
      ? PaymentsAdapter
      : D extends "ai"
        ? AiAdapter
        : D extends "storage"
          ? FileStorageAdapter
          : D extends "admin"
            ? AdminAdapter
            : D extends "logger"
              ? LoggerAdapter
              : never;

type AdapterByDomain = {
  [D in AdapterDomain]: AdapterForDomain<D>;
};

type ResolvedAdapterSet = AdapterByDomain & {
  fileStorage: FileStorageAdapter;
};

type ExplicitAdapterMap = Record<AdapterDomain, boolean>;

const DEFAULT_PROVIDER_BY_DOMAIN: Record<AdapterDomain, string> = {
  db: "localStorage",
  auth: "localStorage",
  payments: "localStorage",
  ai: "localStorage",
  storage: "localStorage",
  admin: "localStorage",
  logger: "console",
};

/**
 * React CSS adapter bag keyed by domain.
 *
 * @property db Database adapter implementation when configured.
 * @property auth Authentication adapter implementation when configured.
 * @property payments Payments adapter implementation when configured.
 * @property ai AI adapter implementation when configured.
 * @property storage File-storage adapter implementation when configured.
 * @property fileStorage File-storage adapter alias accepted by the public React CSS provider.
 * @property admin Admin adapter implementation when configured.
 * @property logger Logger adapter implementation when configured.
 */
export interface AdapterSet extends Partial<Record<AdapterDomain, AnyAdapter>> {
  db?: DbAdapter;
  auth?: AuthAdapter;
  payments?: PaymentsAdapter;
  ai?: AiAdapter;
  storage?: FileStorageAdapter;
  fileStorage?: FileStorageAdapter;
  admin?: AdminAdapter;
  logger?: LoggerAdapter;
}

/** Registry shape accepted by the public React CSS provider. */
export type AdapterRegistry = AdapterSet;

/**
 * React CSS adapter context value exposed to hooks and provider consumers.
 *
 * @property adapters Adapter bag passed into the provider.
 * @property statuses Current status record for each adapter domain.
 * @property getAdapter Returns a configured adapter for a specific domain or throws when missing.
 * @property isReady Reports whether a domain is configured and connected.
 * @property isLoading Indicates whether initial health checks are still running.
 */
export interface AdapterContextValue {
  adapters: ResolvedAdapterSet;
  statuses: Record<AdapterDomain, AdapterStatusInfo>;
  getAdapter: <K extends AdapterDomain>(domain: K) => AdapterByDomain[K];
  isReady: (domain: AdapterDomain) => boolean;
  isLoading: boolean;
}

const AdapterContext = createContext<AdapterContextValue | null>(null);

export type AdapterHealthProbeMap = Partial<{
  [D in AdapterDomain]: (
    adapter: AdapterByDomain[D],
    adapters: ResolvedAdapterSet,
  ) => Promise<void> | void;
}>;

/**
 * Props accepted by the React CSS adapter provider.
 *
 * @property adapters Optional adapter bag made available to descendant hooks and components.
 * @property registry Optional registry alias for adapter overrides.
 * @property db Optional direct database adapter override.
 * @property auth Optional direct auth adapter override.
 * @property payments Optional direct payments adapter override.
 * @property ai Optional direct AI adapter override.
 * @property storage Optional direct file-storage adapter override.
 * @property fileStorage Optional public file-storage adapter alias.
 * @property admin Optional direct admin adapter override.
 * @property logger Optional direct logger adapter override.
 * @property children React subtree that should receive adapter context.
 * @property healthCheck Whether the provider should probe configured adapters on mount.
 * @property healthProbes Optional domain-specific health probes supplied by the host app.
 * @property statusOverrides Optional deterministic status overrides for tests and previews.
 * @property onError Optional typed error callback invoked when provider lifecycle checks fail.
 */
export interface AdapterProviderProps extends Partial<AdapterSet> {
  adapters?: AdapterSet;
  registry?: Partial<AdapterRegistry>;
  children: ReactNode;
  healthCheck?: boolean;
  healthProbes?: AdapterHealthProbeMap;
  statusOverrides?: Partial<Record<AdapterDomain, Partial<AdapterStatusInfo>>>;
  onError?: (error: AdapterError) => void;
}

type AdapterResolutionProps = Pick<
  AdapterProviderProps,
  | "adapters"
  | "registry"
  | "db"
  | "auth"
  | "payments"
  | "ai"
  | "storage"
  | "fileStorage"
  | "admin"
  | "logger"
>;

function createExplicitAdapterMap(
  props: AdapterResolutionProps,
): ExplicitAdapterMap {
  const provided = {
    ...(props.registry ?? {}),
    ...(props.adapters ?? {}),
  };
  const hasStorage =
    props.fileStorage !== undefined ||
    props.storage !== undefined ||
    provided.fileStorage !== undefined ||
    provided.storage !== undefined;

  return {
    db: props.db !== undefined || provided.db !== undefined,
    auth: props.auth !== undefined || provided.auth !== undefined,
    payments: props.payments !== undefined || provided.payments !== undefined,
    ai: props.ai !== undefined || provided.ai !== undefined,
    storage: hasStorage,
    admin: props.admin !== undefined || provided.admin !== undefined,
    logger: props.logger !== undefined || provided.logger !== undefined,
  };
}

function createDefaultAdapterSet(): ResolvedAdapterSet {
  const fileStorage = createLocalStorageFileAdapter();

  return {
    db: createLocalStorageDbAdapter(),
    auth: createLocalStorageAuthAdapter(),
    payments: createLocalStoragePaymentsAdapter(),
    ai: createLocalStorageAiAdapter(),
    storage: fileStorage,
    fileStorage,
    admin: createLocalStorageAdminAdapter(),
    logger: createConsoleLoggerAdapter(),
  };
}

function resolveAdapterSet(
  props: AdapterResolutionProps,
  defaults: ResolvedAdapterSet,
): ResolvedAdapterSet {
  const provided = {
    ...(props.registry ?? {}),
    ...(props.adapters ?? {}),
  };
  const fileStorage =
    props.fileStorage ??
    props.storage ??
    provided.fileStorage ??
    provided.storage ??
    defaults.storage;

  return {
    db: props.db ?? provided.db ?? defaults.db,
    auth: props.auth ?? provided.auth ?? defaults.auth,
    payments: props.payments ?? provided.payments ?? defaults.payments,
    ai: props.ai ?? provided.ai ?? defaults.ai,
    storage: fileStorage,
    fileStorage,
    admin: props.admin ?? provided.admin ?? defaults.admin,
    logger: props.logger ?? provided.logger ?? defaults.logger,
  };
}

function getInitialProviderName(
  domain: AdapterDomain,
  adapters: ResolvedAdapterSet,
  explicitAdapters: ExplicitAdapterMap,
  statusOverrides?: Partial<Record<AdapterDomain, Partial<AdapterStatusInfo>>>,
): string {
  const overrideProvider = statusOverrides?.[domain]?.provider;
  if (overrideProvider !== undefined) {
    return overrideProvider;
  }

  if (!adapters[domain]) {
    return "none";
  }

  if (explicitAdapters[domain] && !isAdaptersConfigured()) {
    return domain === "logger" ? "console" : "configured";
  }

  if (!isAdaptersConfigured()) {
    return DEFAULT_PROVIDER_BY_DOMAIN[domain] ?? "none";
  }

  return (
    getDomainConfig(domain)?.provider ??
    DEFAULT_PROVIDER_BY_DOMAIN[domain] ??
    "none"
  );
}

function createInitialStatuses(
  adapters: ResolvedAdapterSet,
  explicitAdapters: ExplicitAdapterMap,
  healthCheck: boolean,
  statusOverrides?: Partial<Record<AdapterDomain, Partial<AdapterStatusInfo>>>,
): Record<AdapterDomain, AdapterStatusInfo> {
  const initial: Record<AdapterDomain, AdapterStatusInfo> = {
    db: {
      domain: "db",
      provider: "none",
      status: "disconnected",
      lastCheckedAt: Date.now(),
    },
    auth: {
      domain: "auth",
      provider: "none",
      status: "disconnected",
      lastCheckedAt: Date.now(),
    },
    payments: {
      domain: "payments",
      provider: "none",
      status: "disconnected",
      lastCheckedAt: Date.now(),
    },
    ai: {
      domain: "ai",
      provider: "none",
      status: "disconnected",
      lastCheckedAt: Date.now(),
    },
    storage: {
      domain: "storage",
      provider: "none",
      status: "disconnected",
      lastCheckedAt: Date.now(),
    },
    admin: {
      domain: "admin",
      provider: "none",
      status: "disconnected",
      lastCheckedAt: Date.now(),
    },
    logger: {
      domain: "logger",
      provider: "none",
      status: "disconnected",
      lastCheckedAt: Date.now(),
    },
  };

  for (const domain of ADAPTER_DOMAINS) {
    const adapter = adapters[domain];
    initial[domain] = {
      domain,
      provider: getInitialProviderName(
        domain,
        adapters,
        explicitAdapters,
        statusOverrides,
      ),
      status: adapter
        ? healthCheck
          ? "initializing"
          : "connected"
        : "disconnected",
      lastCheckedAt: Date.now(),
      ...statusOverrides?.[domain],
    };
  }

  return initial;
}

function toAdapterError(domain: AdapterDomain, error: unknown): AdapterError {
  if (error instanceof AdapterError) {
    return error;
  }

  const message =
    error instanceof Error ? error.message : "Health check failed";
  return new AdapterError(message, domain, "SERVICE_ERROR", error);
}

async function probeAdapterDomain(
  domain: AdapterDomain,
  adapters: ResolvedAdapterSet,
  healthProbes?: AdapterHealthProbeMap,
): Promise<{ connected: true } | { connected: false; error: string }> {
  const customProbe = healthProbes?.[domain];
  if (customProbe) {
    const runCustomProbe = customProbe as (
      adapter: AdapterByDomain[AdapterDomain],
      adapters: ResolvedAdapterSet,
    ) => Promise<void> | void;
    await runCustomProbe(adapters[domain], adapters);
    return { connected: true };
  }

  switch (domain) {
    case "auth":
      await adapters.auth.getSession();
      return { connected: true };
    case "payments":
      await adapters.payments.getPlans();
      return { connected: true };
    case "admin":
      await adapters.admin.getMetrics();
      return { connected: true };
    case "logger":
      await adapters.logger.child({ healthCheck: true });
      return { connected: true };
    case "db":
      await adapters.db.count("__geenius_adapter_healthcheck");
      return { connected: true };
    case "storage":
      await adapters.storage.list("__geenius_adapter_healthcheck");
      return { connected: true };
    case "ai":
      await adapters.ai.complete("geenius adapter health check");
      return { connected: true };
  }

  return {
    connected: false,
    error: `No health probe is registered for ${domain}.`,
  };
}

/**
 * Provides the React CSS adapter context and optional health-check lifecycle.
 *
 * @param props Provider props including the adapter bag, child tree, and optional health-check settings.
 * @returns A React CSS context provider that exposes adapters and derived status metadata.
 */
// @doc
export function AdapterProvider(props: AdapterProviderProps): JSX.Element {
  const {
    children,
    healthCheck = false,
    statusOverrides,
    adapters: providedAdapters,
    registry,
    db,
    auth,
    payments,
    ai,
    storage,
    fileStorage,
    admin,
    logger,
    healthProbes,
    onError,
  } = props;
  const defaultAdapters = useMemo(createDefaultAdapterSet, []);
  const adapterResolutionProps = useMemo(
    () => ({
      adapters: providedAdapters,
      registry,
      db,
      auth,
      payments,
      ai,
      storage,
      fileStorage,
      admin,
      logger,
    }),
    [
      providedAdapters,
      registry,
      db,
      auth,
      payments,
      ai,
      storage,
      fileStorage,
      admin,
      logger,
    ],
  );
  const explicitAdapters = useMemo(
    () => createExplicitAdapterMap(adapterResolutionProps),
    [adapterResolutionProps],
  );
  const adapters = useMemo(
    () => resolveAdapterSet(adapterResolutionProps, defaultAdapters),
    [adapterResolutionProps, defaultAdapters],
  );
  const [isLoading, setIsLoading] = useState(healthCheck);
  const [statuses, setStatuses] = useState<
    Record<AdapterDomain, AdapterStatusInfo>
  >(() =>
    createInitialStatuses(
      adapters,
      explicitAdapters,
      healthCheck,
      statusOverrides,
    ),
  );

  useEffect(() => {
    setStatuses(
      createInitialStatuses(
        adapters,
        explicitAdapters,
        healthCheck,
        statusOverrides,
      ),
    );
    setIsLoading(healthCheck);
  }, [adapters, explicitAdapters, healthCheck, statusOverrides]);

  useEffect(() => {
    if (!healthCheck) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const checks = ADAPTER_DOMAINS.map(async (domain) => {
      const adapter = adapters[domain];
      if (!adapter) {
        return;
      }

      try {
        if (cancelled) {
          return;
        }

        setStatuses((previous) => ({
          ...previous,
          [domain]: { ...previous[domain], status: "initializing" },
        }));

        const result = await probeAdapterDomain(domain, adapters, healthProbes);
        if (cancelled) {
          return;
        }

        if (!result.connected) {
          setStatuses((previous) => ({
            ...previous,
            [domain]: {
              ...previous[domain],
              status: "disconnected",
              error: result.error,
              lastCheckedAt: Date.now(),
            },
          }));
          return;
        }

        setStatuses((previous) => ({
          ...previous,
          [domain]: {
            ...previous[domain],
            status: "connected",
            lastCheckedAt: Date.now(),
          },
        }));
      } catch (error) {
        const adapterError = toAdapterError(domain, error);
        onError?.(adapterError);

        if (cancelled) {
          return;
        }

        setStatuses((previous) => ({
          ...previous,
          [domain]: {
            ...previous[domain],
            status: "error",
            error: adapterError.message,
            lastCheckedAt: Date.now(),
          },
        }));
      }
    });

    Promise.allSettled(checks).finally(() => {
      if (!cancelled) {
        setIsLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [adapters, healthCheck, healthProbes, onError]);

  const getAdapter = useCallback(
    <K extends AdapterDomain>(domain: K): AdapterByDomain[K] => {
      const adapter = adapters[domain];
      if (!adapter) {
        throw new AdapterContextError(
          `${DOMAIN_LABELS[domain] ?? domain} adapter not configured in <AdapterProvider>`,
          "ADAPTER_NOT_CONFIGURED",
        );
      }
      return adapter;
    },
    [adapters],
  );

  const isReady = useCallback(
    (domain: AdapterDomain) =>
      !!adapters[domain] && statuses[domain]?.status === "connected",
    [adapters, statuses],
  );

  const value = useMemo<AdapterContextValue>(
    () => ({
      adapters,
      statuses,
      getAdapter,
      isReady,
      isLoading,
    }),
    [adapters, statuses, getAdapter, isReady, isLoading],
  );

  return (
    <AdapterContext.Provider value={value}>{children}</AdapterContext.Provider>
  );
}

/**
 * Plural provider alias matching the public `@geenius/adapters/react-css` contract.
 */
// @doc
export const AdaptersProvider = AdapterProvider;

/**
 * Reads the React CSS adapter context from the nearest provider.
 *
 * @returns The active adapter context value with adapters, statuses, and readiness helpers.
 * @throws {AdapterContextError} When called outside of an `<AdapterProvider>` tree.
 */
// @doc
export function useAdapterContext(): AdapterContextValue {
  const context = useContext(AdapterContext);
  if (!context) {
    throw new AdapterContextError(
      "useAdapterContext must be used within <AdapterProvider>",
      "MISSING_CONTEXT",
    );
  }
  return context;
}
